import type { BookmarkView } from '@zyx/shared';
import { Pencil, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { useApi } from '@/hooks/use-connection';
import { useDeleteBookmark, useUpdateBookmark } from '@/hooks/use-library';
import { editionLabel, formatRuntime } from '@/lib/format';
import { urls } from '@/lib/urls';
import { AssetImage } from './asset';
import { tileAt, tileStyle, useSpriteTiles } from './scene-strip';

/** The frame a bookmark points at, from the episode's scrubbing sprite; the cover until that loads, or if there is none. */
function BookmarkFrame(props: { readonly bookmark: BookmarkView }) {
  const api = useApi();
  const { bookmark } = props;
  const strip = useSpriteTiles(bookmark);
  const tile = strip.data === undefined ? undefined : tileAt(strip.data.tiles, bookmark.positionS);
  const time = formatRuntime(bookmark.positionS) ?? '0:00';
  return (
    <div className="relative aspect-video overflow-hidden rounded-md bg-muted">
      {tile === undefined || strip.data === undefined || bookmark.previewSprite === undefined ? (
        <AssetImage file={bookmark.cover} alt={bookmark.code} className="size-full" />
      ) : (
        <div className="size-full" style={{ ...tileStyle(tile, strip.data.size, api.assetUrl(bookmark.previewSprite)), aspectRatio: undefined }} />
      )}
      <span className="absolute bottom-1 right-1 rounded bg-black/75 px-1.5 py-0.5 font-mono text-[11px] text-white">{time}</span>
    </div>
  );
}

/** A label, edited in place: Enter saves, Escape or leaving the field cancels. */
function Label(props: { readonly bookmark: BookmarkView }) {
  const update = useUpdateBookmark();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const save = (event: FormEvent) => {
    event.preventDefault();
    update.mutate({ bookmarkId: props.bookmark.bookmarkId, label: text.trim() === '' ? null : text.trim() }, { onSuccess: () => setEditing(false) });
  };
  if (editing) {
    return (
      <form onSubmit={save}>
        <Input
          autoFocus
          value={text}
          maxLength={200}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => (event.key === 'Escape' ? setEditing(false) : undefined)}
          onBlur={() => (update.isPending ? undefined : setEditing(false))}
          placeholder="What happens here"
          aria-label="Bookmark label"
          className="h-7 text-xs"
        />
      </form>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        setText(props.bookmark.label ?? '');
        setEditing(true);
      }}
      className="group/label flex w-full min-w-0 items-center gap-1 text-left text-xs"
      title="Edit the label"
    >
      <span className={props.bookmark.label === undefined ? 'text-muted-foreground' : 'truncate'}>{props.bookmark.label ?? 'Add a label'}</span>
      <Pencil className="size-3 shrink-0 text-muted-foreground opacity-0 group-hover/label:opacity-100" />
    </button>
  );
}

function Remove(props: { readonly bookmark: BookmarkView }) {
  const remove = useDeleteBookmark();
  return (
    <button
      type="button"
      aria-label="Delete bookmark"
      title="Delete bookmark"
      disabled={remove.isPending}
      onClick={() => remove.mutate(props.bookmark.bookmarkId)}
      className="rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-destructive focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring group-hover:opacity-100"
    >
      <Trash2 className="size-3.5" />
    </button>
  );
}

/** Where in the movie: the time, and the part and edition when the movie has more than one. */
function where(bookmark: BookmarkView, detail: { readonly parts: boolean; readonly editions: boolean }): string {
  return [formatRuntime(bookmark.positionS) ?? '0:00', detail.parts ? `part ${bookmark.episodeNo}` : undefined, detail.editions ? editionLabel(bookmark.edition) : undefined]
    .filter((part) => part !== undefined)
    .join(' · ');
}

export const BOOKMARK_GRID = 'grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6';

/**
 * One movie's bookmarks, in playing order. `onPlay` is given the bookmark to start from;
 * one on an edition the movie no longer has is shown but cannot play.
 */
export function MovieBookmarks(props: {
  readonly bookmarks: ReadonlyArray<BookmarkView>;
  readonly playable: (bookmark: BookmarkView) => boolean;
  readonly detail: { readonly parts: boolean; readonly editions: boolean };
  readonly onPlay: (bookmark: BookmarkView) => void;
}) {
  return (
    <div className={BOOKMARK_GRID}>
      {props.bookmarks.map((bookmark) => (
        <div key={bookmark.bookmarkId} className="group space-y-1.5">
          <button
            type="button"
            disabled={!props.playable(bookmark)}
            onClick={() => props.onPlay(bookmark)}
            aria-label={`Play from ${formatRuntime(bookmark.positionS) ?? '0:00'}`}
            className="block w-full rounded-md ring-offset-background hover:ring-2 hover:ring-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:ring-0"
          >
            <BookmarkFrame bookmark={bookmark} />
          </button>
          <div className="flex items-start gap-1">
            <div className="min-w-0 flex-1 space-y-0.5">
              <Label bookmark={bookmark} />
              {props.detail.parts || props.detail.editions ? <p className="text-[11px] text-muted-foreground">{where(bookmark, props.detail)}</p> : null}
            </div>
            <Remove bookmark={bookmark} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Every bookmark, newest first, each opening its movie at that moment. */
export function AllBookmarks(props: { readonly bookmarks: ReadonlyArray<BookmarkView> }) {
  return (
    <div className={BOOKMARK_GRID}>
      {props.bookmarks.map((bookmark) => (
        <div key={bookmark.bookmarkId} className="group space-y-1.5">
          <Link
            to={urls.movieAt(bookmark.code, bookmark)}
            className="block rounded-md ring-offset-background hover:ring-2 hover:ring-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Play ${bookmark.code} from ${formatRuntime(bookmark.positionS) ?? '0:00'}`}
          >
            <BookmarkFrame bookmark={bookmark} />
          </Link>
          <div className="flex items-start gap-1">
            <div className="min-w-0 flex-1 space-y-0.5">
              <Link to={urls.movie(bookmark.code)} className="block truncate text-xs hover:underline" title={bookmark.title}>
                <span className="font-mono font-semibold text-primary">{bookmark.code}</span> <span className="text-foreground/80">{bookmark.title}</span>
              </Link>
              <Label bookmark={bookmark} />
            </div>
            <Remove bookmark={bookmark} />
          </div>
        </div>
      ))}
    </div>
  );
}
